import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, LayoutGrid, List, Gauge, Crosshair, CheckSquare, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PIPELINE, STAGE_LABEL, STAGE_INDEX, stageFarge, SIGNAL_MAP, fmtSiden, dagerSiden } from "./eventosUtils";
import { ReadinessBadge } from "./Readiness";
import { BulkBar } from "./BulkBar";
import { gruppeFor } from "./OutreachListe";
import SpeakerDrawer from "./SpeakerDrawer";
import NySpeakerModal from "./NySpeakerModal";
import TargetsView from "./TargetsView";
import OutreachListe from "./OutreachListe";

const feil = (e) => toast.error(formatApiError(e?.response?.data?.detail) || "Noe gikk galt");

const Kort = ({ sp, eier, onOpen, velgModus, valgt, onVelg }) => {
  const [dras, setDras] = useState(false);
  return (
    <button
      type="button"
      draggable={!velgModus}
      onDragStart={(e) => { e.dataTransfer.setData("text/speaker", sp.id); setDras(true); }}
      onDragEnd={() => setDras(false)}
      onClick={() => (velgModus ? onVelg(sp.id) : onOpen(sp.id))}
      className={`sak-kort w-full text-left bg-white border rounded-[10px] px-3 py-2.5 ${dras ? "dras" : ""} ${
        valgt ? "border-neutral-900 ring-1 ring-neutral-900" : "border-[#E7E4DB]"
      }`}
      data-testid={`speaker-kort-${sp.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-neutral-900 leading-snug">{sp.person?.name}</p>
        {!velgModus && eier && (
          <span className="shrink-0 mt-0.5" title={`Ansvarlig: ${eier.name}`} data-testid={`speaker-kort-eier-${sp.id}`}>
            {eier.bilde
              ? <img src={eier.bilde} alt="" className="w-[18px] h-[18px] rounded-full object-cover" />
              : (
                <span className="w-[18px] h-[18px] rounded-full text-[9px] font-bold text-white inline-flex items-center justify-center" style={{ background: eier.color || "#8B5CF6" }}>
                  {eier.name[0]}
                </span>
              )}
          </span>
        )}
        {velgModus && (
          <span className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 mt-0.5 ${
            valgt ? "bg-neutral-900 border-neutral-900 text-white" : "border-[#D8D4C8] text-transparent"
          }`} data-testid={`speakersos-velg-kort-${sp.id}`}>
            <Check size={10} />
          </span>
        )}
      </div>
      {sp.person?.org && <p className="text-[10.5px] text-neutral-400 mt-0.5 truncate">{sp.person.org}</p>}
      <div className="flex items-center gap-2 mt-2">
        <ReadinessBadge computed={sp.computed} />
        {sp.signal && SIGNAL_MAP[sp.signal] && (
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: SIGNAL_MAP[sp.signal].farge }} title={`Signal: ${SIGNAL_MAP[sp.signal].label}`} />
        )}
        {sp.lastContact && <span className="text-[9.5px] text-neutral-400 tabular-nums" title="Sist kontaktet">{fmtSiden(sp.lastContact)}</span>}
        {sp.published && <span className="w-1.5 h-1.5 rounded-full bg-[#F1E75A] border border-neutral-400 ml-auto" title="Publisert på nettsiden" />}
        {sp.openTasks > 0 && <span className="text-[10px] text-neutral-400">{sp.openTasks} saker</span>}
      </div>
    </button>
  );
};

export default function SpeakersOS() {
  const [data, setData] = useState(null);
  const [visning, setVisning] = useState("pipeline");
  const [aapen, setAapen] = useState(null);
  const [nyModal, setNyModal] = useState(false);
  const [draOver, setDraOver] = useState(null);
  const [velgModus, setVelgModus] = useState(false);
  const [valgte, setValgte] = useState(() => new Set());
  const [fokus, setFokus] = useState(null);
  const [targets, setTargets] = useState([]);
  const aktor = localStorage.getItem("nas_aktor") || "Admin";
  const eierMap = useMemo(() => Object.fromEntries((data?.members || []).map((m) => [m.id, m])), [data]);

  const velgToggle = (id) => setValgte((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const load = useCallback(async () => {
    try {
      const [{ data: d }, t] = await Promise.all([
        api.get("/admin/os/speakers"),
        api.get("/admin/os/targets").catch(() => null),
      ]);
      setData(d);
      if (t) setTargets(t.data.targets || []);
    } catch (e) {
      feil(e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data) return;
    const p = new URLSearchParams(window.location.search).get("aapne");
    if (p && data.speakers.some((s) => s.id === p)) {
      setAapen(p);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [data]);

  const patch = async (pid, p) => {
    try {
      const { data: r } = await api.patch(`/admin/os/speakers/${pid}`, { ...p, actor: aktor });
      setData((prev) => ({
        ...prev,
        speakers: prev.speakers.map((s) => (s.id === pid ? { ...s, ...r.item, sessions: s.sessions, openTasks: s.openTasks } : s)),
      }));
    } catch (e) {
      feil(e);
    }
  };

  const opprett = async (payload) => {
    try {
      await api.post("/admin/os/speakers", { ...payload, actor: aktor });
      setNyModal(false);
      toast.success("Speaker lagt til");
      await load();
    } catch (e) {
      feil(e);
      throw e;
    }
  };

  const bulkPatch = async (ids, p) => {
    await Promise.all(ids.map((id) => patch(id, p)));
  };

  const bulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/admin/os/speakers/${id}`)));
      setAapen(null);
      setData((prev) => ({ ...prev, speakers: prev.speakers.filter((s) => !ids.includes(s.id)) }));
      toast.success(`${ids.length} fjernet fra pipelinen`);
    } catch (e) {
      feil(e);
      await load();
    }
  };

  const bulkStadium = async (k) => {
    const ids = [...valgte];
    await bulkPatch(ids, { stage: k });
    toast.success(`${ids.length} flyttet til ${STAGE_LABEL[k]}`);
    setValgte(new Set());
  };
  const bulkAnsvarlig = async (mid, navn) => {
    const ids = [...valgte];
    await bulkPatch(ids, { ownerId: mid });
    toast.success(mid ? `${ids.length} tildelt ${navn}` : `Ansvarlig fjernet på ${ids.length}`);
    setValgte(new Set());
  };
  const bulkSlettValgte = async () => {
    const ids = [...valgte];
    if (!window.confirm(`Slette ${ids.length} fra pipelinen? Dette kan ikke angres.`)) return;
    await bulkDelete(ids);
    setValgte(new Set());
    setVelgModus(false);
  };

  const slett = async (pid) => {
    if (!window.confirm("Fjerne speakeren fra pipelinen? Personen beholdes.")) return;
    try {
      await api.delete(`/admin/os/speakers/${pid}`);
      setAapen(null);
      setData((prev) => ({ ...prev, speakers: prev.speakers.filter((s) => s.id !== pid) }));
      toast.success("Fjernet fra pipelinen");
    } catch (e) {
      feil(e);
    }
  };

  const aapenSpeaker = useMemo(() => data?.speakers.find((s) => s.id === aapen) || null, [data, aapen]);

  if (!data) {
    return <p className="text-center text-xs text-neutral-400 py-20" data-testid="speakersos-laster">Laster speakers …</p>;
  }

  const bekreftet = data.speakers.filter((s) => s.stage === "confirmed").length;
  const forfalte = data.speakers.filter((s) => s.stage !== "declined" && s.followUp && (dagerSiden(s.followUp) ?? -1) >= 0).length;
  const purre = data.speakers.filter((s) => gruppeFor(s) === "venter" && (dagerSiden(s.lastContact) ?? -1) > 10).length;
  const blockere = data.speakers.filter((s) => s.stage !== "declined" && (STAGE_INDEX[s.stage] ?? 0) >= 5 && (s.computed?.blockers || 0) > 0).length;
  const aaVurdere = targets.filter((x) => !x.archived && x.status !== "converted" && x.status !== "declined").length;

  const gaaTil = (k, f = null) => {
    setVisning(k);
    setFokus(f);
    setVelgModus(false);
    setValgte(new Set());
  };

  const pulsItems = [
    forfalte > 0 && { id: "forfalt", tekst: `${forfalte} oppfølging${forfalte > 1 ? "er" : ""} å ta`, dot: "#D97706", til: () => gaaTil("liste", "forfalt") },
    purre > 0 && { id: "purre", tekst: `${purre} venter svar >10 d`, dot: "#0EA5E9", til: () => gaaTil("liste", "purre") },
    blockere > 0 && { id: "blockere", tekst: `${blockere} med readiness-blockere`, dot: "#DC2626", til: () => gaaTil("readiness") },
    aaVurdere > 0 && { id: "targets", tekst: `${aaVurdere} targets å vurdere`, dot: "#B8860B", til: () => gaaTil("targets") },
  ].filter(Boolean);

  return (
    <div data-testid="speakersos-admin">
      <header className="flex flex-wrap items-end gap-3 mb-5">
        <div className="mr-auto">
          <p className="adm-eyebrow mb-1.5">Event 2027</p>
          <h1 className="adm-title">Speakers</h1>
          <p className="adm-sub mt-1.5" data-testid="speakersos-teller">
            {bekreftet} bekreftet · {data.speakers.length} i pipelinen
          </p>
        </div>
        <div className="flex bg-white border border-[#E7E4DB] rounded-lg p-0.5">
          {[
            { k: "pipeline", label: "Pipeline", Ico: LayoutGrid },
            { k: "liste", label: "Outreach", Ico: List, badge: forfalte, farge: "#D97706" },
            { k: "readiness", label: "Readiness", Ico: Gauge, badge: blockere, farge: "#DC2626" },
            { k: "targets", label: "Targets", Ico: Crosshair, badge: aaVurdere, farge: "#B8860B" },
          ].map(({ k, label, Ico, badge, farge }) => (
            <button
              key={k}
              type="button"
              onClick={() => gaaTil(k)}
              className={`inline-flex items-center gap-1.5 text-[12px] font-medium px-2.5 py-1.5 rounded-md transition-all ${visning === k ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-800"}`}
              data-testid={`speakersos-view-${k}`}
            >
              <Ico size={13} /> {label}
              {badge > 0 && (
                <span className="text-[10px] font-bold tabular-nums" style={{ color: visning === k ? "#fff" : farge }} data-testid={`speakersos-badge-${k}`}>
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
        {visning === "pipeline" && (
          <button
            type="button"
            onClick={() => { setValgte(new Set()); setVelgModus((v) => !v); }}
            className={velgModus ? "adm-btn" : "adm-btn-ghost"}
            data-testid="speakersos-velg-btn"
          >
            <CheckSquare size={13} /> {velgModus ? "Avbryt" : "Velg"}
          </button>
        )}
        {visning !== "targets" && (
          <button type="button" onClick={() => setNyModal(true)} className="adm-btn" data-testid="speakersos-ny-btn">
            <Plus size={14} /> Ny speaker
          </button>
        )}
      </header>

      {pulsItems.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="speakersos-puls">
          {pulsItems.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={p.til}
              className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-neutral-700 rounded-full px-3 py-1.5 border border-[#E7E4DB] bg-white hover:border-neutral-400 transition-colors"
              data-testid={`speakersos-puls-${p.id}`}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.dot }} />
              {p.tekst}
            </button>
          ))}
        </div>
      )}

      {visning === "pipeline" && (
        <div className="flex gap-2.5 overflow-x-auto pb-2 snap-x sak-noscroll h-[calc(100dvh-250px)] min-h-[420px]" data-testid="speakersos-pipeline">
          {PIPELINE.map((st) => {
            const gruppe = data.speakers.filter((s) => s.stage === st.k);
            return (
              <div
                key={st.k}
                className={`w-[230px] sm:w-[220px] shrink-0 snap-start rounded-xl bg-[#F1EFE8] flex flex-col transition-colors ${draOver === st.k ? "bg-[#ECEADE]" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDraOver(st.k); }}
                onDragLeave={() => setDraOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDraOver(null);
                  const id = e.dataTransfer.getData("text/speaker");
                  const sp = data.speakers.find((x) => x.id === id);
                  if (sp && sp.stage !== st.k) patch(id, { stage: st.k });
                }}
                data-testid={`speakersos-kolonne-${st.k}`}
              >
                <div className="flex items-center gap-1.5 px-3.5 pt-2.5 pb-2 shrink-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stageFarge(st.k) }} />
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-600 truncate">{st.label}</span>
                  <span className="text-[10.5px] tabular-nums text-neutral-400 ml-auto">{gruppe.length}</span>
                </div>
                <div className="flex-1 overflow-y-auto sak-noscroll px-2 pb-2 flex flex-col gap-1.5">
                  {gruppe.map((sp) => (
                    <Kort key={sp.id} sp={sp} eier={sp.ownerId ? eierMap[sp.ownerId] : null} onOpen={setAapen} velgModus={velgModus} valgt={valgte.has(sp.id)} onVelg={velgToggle} />
                  ))}
                  {gruppe.length === 0 && (
                    <div className="flex-1 min-h-[90px] m-0.5 border border-dashed border-[#E7E4DB] rounded-lg flex items-center justify-center">
                      <span className="text-[10px] text-neutral-300">Slipp kort her</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {visning === "liste" && (
        <OutreachListe speakers={data.speakers} members={data.members} onOpen={setAapen} onPatch={patch} onBulkPatch={bulkPatch} onBulkDelete={bulkDelete} fokus={fokus} onFjernFokus={() => setFokus(null)} />
      )}

      {visning === "readiness" && (
        <div className="adm-card overflow-hidden" data-testid="speakersos-readiness">
          <table className="adm-table">
            <thead>
              <tr>
                <th>Navn</th>
                <th>Stadium</th>
                <th>Readiness</th>
                <th>Blockers</th>
                <th>Utestående</th>
              </tr>
            </thead>
            <tbody>
              {[...data.speakers]
                .sort((a, b) => (b.computed?.blockers || 0) - (a.computed?.blockers || 0) || (a.computed?.completeness || 0) - (b.computed?.completeness || 0))
                .map((sp) => {
                  const utestaende = (sp.computed?.items || []).filter((i) => i.state === "open" && !i.blocker).map((i) => i.label);
                  return (
                    <tr key={sp.id} onClick={() => setAapen(sp.id)} className="cursor-pointer" data-testid={`speakersos-ready-rad-${sp.id}`}>
                      <td className="font-medium">{sp.person?.name}</td>
                      <td className="text-[11.5px]" style={{ color: stageFarge(sp.stage) }}>{STAGE_LABEL[sp.stage]}</td>
                      <td><ReadinessBadge computed={sp.computed} size="md" /></td>
                      <td className="text-[12px] text-red-600">{(sp.computed?.blockerLabels || []).join(", ") || "—"}</td>
                      <td className="text-[12px] text-neutral-500">{utestaende.join(", ") || "—"}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {visning === "targets" && <TargetsView members={data.members} aktor={aktor} onConverted={load} />}

      {visning === "pipeline" && velgModus && (
        <BulkBar
          antall={valgte.size}
          alleValgt={valgte.size === data.speakers.length}
          onVelgAlle={() => setValgte(valgte.size === data.speakers.length ? new Set() : new Set(data.speakers.map((s) => s.id)))}
          stages={PIPELINE}
          stageFarge={stageFarge}
          stageLabel={STAGE_LABEL}
          members={data.members}
          onStadium={bulkStadium}
          onAnsvarlig={bulkAnsvarlig}
          onSlett={bulkSlettValgte}
          onLukk={() => { setVelgModus(false); setValgte(new Set()); }}
          t="pipeline"
        />
      )}

      {aapenSpeaker && (
        <SpeakerDrawer
          speaker={aapenSpeaker}
          members={data.members}
          aktor={aktor}
          onPatch={(p) => patch(aapenSpeaker.id, p)}
          onReload={load}
          onDelete={() => slett(aapenSpeaker.id)}
          onLukk={() => setAapen(null)}
        />
      )}
      {nyModal && (
        <NySpeakerModal persons={data.persons} members={data.members} onLagre={opprett} onLukk={() => setNyModal(false)} />
      )}
    </div>
  );
}
